import React, { useMemo, useEffect, useState, useRef } from "react";
//Import Breadcrumb
import { Container } from "reactstrap";
import "react-toastify/dist/ReactToastify.css";

import {
  ListingStatusUpdate,
  FetchListingsRequests,
  DownloadCSV,
} from "../../apis/Property";
import { Search, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../../components/shared/dialog";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/shared/alertDialog";

import Paginate from "../../components/shared/paginate";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { GenerateShortToken } from "../../apis/Auth";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const EditRequests = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.MANAGE_LISTINGS);
  const [properties, setProperties] = useState([]);
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [dataInCSV, setDataInCSV] = useState("");
  const [keyword, setKeyword] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [propertyId, setPropertyId] = useState("");
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const [openAlertDialog, setOpenAlertDialog] = useState(false);

  const limit = "10";

  // short token cache + helpers
  const [shortToken, setShortToken] = useState("");
  const tokenTimestampRef = useRef(0);
  const generatingPromiseRef = useRef(null);
  const TOKEN_TTL_MS = 3 * 60 * 1000; // 3 minutes

  useEffect(() => {
    const generateShortToken = async () => {
      try {
        const response = await GenerateShortToken();
        const token = response.data?.token || "";
        setShortToken(token);
        tokenTimestampRef.current = Date.now();
      } catch (err) {
        toast.error("Failed to generate short token");
      }
    };
    generateShortToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getValidShortToken = async () => {
    if (
      shortToken &&
      tokenTimestampRef.current &&
      Date.now() - tokenTimestampRef.current < TOKEN_TTL_MS
    ) {
      return shortToken;
    }

    if (generatingPromiseRef.current) {
      try {
        await generatingPromiseRef.current;
        return shortToken;
      } catch {
        return "";
      }
    }

    generatingPromiseRef.current = (async () => {
      try {
        const response = await GenerateShortToken();
        const token = response.data?.token || "";
        setShortToken(token);
        tokenTimestampRef.current = Date.now();
        return token;
      } catch (err) {
        toast.error("Failed to generate short token");
        return "";
      } finally {
        generatingPromiseRef.current = null;
      }
    })();

    const newToken = await generatingPromiseRef.current;
    return newToken || "";
  };

  const fetchEditRequests = async () => {
    const response = await FetchListingsRequests(currentPage, limit, "edit");
    setProperties(response?.data?.listings);
    setFilteredProperties(response?.data?.listings);
    setTotalPages(response?.data?.totalPages);
  };

  const downloadCSV = () => {
    DownloadCSV().then((res) => {
      setDataInCSV(res);
    });
  };

  useEffect(() => {
    fetchEditRequests();
    downloadCSV();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (keyword) {
      //TODO: call search API
      console.log("search");
    } else {
      fetchEditRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (keyword) {
      setCurrentPage("1");
      console.log("searched");
      // TODO: call search API
    } else fetchEditRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const updateEditRequest = async (propertyId, status, reason) => {
    const response = await ListingStatusUpdate(propertyId, status, reason);
    if (response?.success) {
      toast.success("Edit request " + status);
      setReasonError(false);
      setOpenDialog(false);
      setFilteredProperties((prev) =>
        prev.filter((property) => property._id !== propertyId),
      );
      setProperties((prev) =>
        prev.filter((property) => property._id !== propertyId),
      );
    }
  };

  const declineEditRequest = async () => {
    if (!reason) {
      setReasonError(true);
      return;
    }
    if (!propertyId) {
      toast.error("Select an edit request you want to decline");
      return;
    }
    updateEditRequest(propertyId, "declined", reason);
  };

  const approveEditRequest = async () => {
    if (!propertyId) {
      toast.error("Select an edit request you want to approve");
      return;
    }
    updateEditRequest(propertyId, "approved");
  };

  const handlePropertyStatus = async (propertyId, status) => {
    setPropertyId(propertyId);
    if (status === "declined") {
      setOpenDialog(true);
    } else {
      setOpenAlertDialog(true);
    }
  };

  const columns = useMemo(
    () => [
      "ID",
      "Title",
      "Host Name",
      "Host Email",
      "Address",
      "City",
      "State",
      "Country",
      "Updated At",
      "Published",
      "Ready",
      "Edit",
      "View",
      "Status",
    ],
    [],
  );

  const obscureEmail = (email) => {
    return email;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6 flex flex-col gap-6 ">
            <h1 className="text-2xl dark:text-[#c7d8eb]">
              Manage edit requests
            </h1>
            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full flex flex-col lg:flex-row justify-between gap-4 p-4">
                <div className="relative border max-w-[400px] border-gray-500 dark:border-white rounded-md w-full">
                  <Search
                    size={16}
                    className="absolute left-2 bottom-1/2 translate-y-1/2"
                  />
                  <input
                    className="pl-8 pr-6 py-2 text-base rounded-md w-full dark:bg-darkBg2"
                    type="search"
                    placeholder="Search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </div>
                <div className="flex lg:justify-self-end">
                  <a
                    href={`data:text/csv;charset=utf-8,${escape(dataInCSV)}`}
                    download="edit-requests-list.csv"
                  >
                    <div className="flex items-center gap-2">
                      Export data into CSV
                      <div className="shrink-0 bg-white p-1">
                        <Upload className="" size={16} />
                      </div>
                    </div>
                  </a>
                </div>
              </div>
              <div className="overflow-x-auto ">
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className={`${
                        index === 0 ? "w-[12%]" : "w-[11%]"
                      } min-w-[130px] content-center border-y border-gray-500 dark:border-white break-words py-2 text-center font-semibold`}
                    >
                      {column}
                    </div>
                  ))}
                </div>
                {properties &&
                  filteredProperties.map((property, index) => (
                    <>
                      <div
                        key={`property-desktop-${index}`}
                        className="hidden lg:flex"
                      >
                        <div className="w-[6.2%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property._id}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.title}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.host?.firstName}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {obscureEmail(property.host?.email)}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.location?.street &&
                          property.location?.city?.en &&
                          property.location?.state &&
                          property.location?.country &&
                          property.location?.zip
                            ? property.location?.street +
                              ", " +
                              property.location?.city?.en +
                              ", " +
                              property.location?.state +
                              ", " +
                              property.location?.country?.en +
                              ", " +
                              property.location?.zip
                            : ""}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.location?.city?.en}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.location?.state}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.location?.country?.en}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.updatedAt?.toString()?.slice(0, 10)}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.published ? "Yes" : "No"}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.ready ? "Yes" : "No"}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {canManage ? (
                            <button
                              type="button"
                              className="text-secondaryColor"
                              onClick={async () => {
                                const token = await getValidShortToken();
                                if (token) {
                                  const url = `${process.env.REACT_APP_CLIENTSIDE_URL}/listing/create?propertyId=${property._id}&token=${token}`;
                                  window.open(url, "_blank");
                                }
                              }}
                            >
                              Edit
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <button
                            type="button"
                            className="text-secondaryColor"
                            onClick={async () => {
                              const token = await getValidShortToken();
                              if (token) {
                                const url = `${process.env.REACT_APP_CLIENTSIDE_URL}/rooms/${property._id}/preview?token=${token}`;
                                window.open(url, "_blank");
                              }
                            }}
                          >
                            View
                          </button>
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <select
                            value={property.status}
                            onChange={(event) =>
                              handlePropertyStatus(
                                property._id,
                                event.target.value,
                              )
                            }
                            className="p-2 border border-gray-500 dark:border-white rounded-md dark:bg-darkBg2"
                          >
                            <option value={"pending"} disabled>
                              Select
                            </option>
                            <option value={"declined"}>Decline</option>
                            <option value={"approved"}>Approve</option>
                          </select>
                        </div>
                      </div>
                      <div
                        key={`property-mobile-${index}`}
                        className="block lg:hidden mb-6 border-x border-y border-gray-500 dark:border-white"
                      >
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">ID</div>
                          <div>{property._id}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Title</div>
                          <div>{property.title}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Host Name</div>
                          <div>{property.host?.firstName}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Host Email</div>
                          <div>{obscureEmail(property.host?.email)}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Address</div>
                          <div>
                            {property.location?.street &&
                            property.location?.city?.en &&
                            property.location?.state &&
                            property.location?.country &&
                            property.location?.zip
                              ? property.location?.street +
                                ", " +
                                property.location?.city?.en +
                                ", " +
                                property.location?.state +
                                ", " +
                                property.location?.country?.en +
                                ", " +
                                property.location?.zip
                              : ""}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">city</div>
                          <div>{property.location?.city?.en}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">State</div>
                          <div>{property.location?.state}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Country</div>
                          <div>{property.location?.country?.en}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Updated At</div>
                          <div>
                            {property.updatedAt?.toString()?.slice(0, 10)}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Published</div>
                          <div>{property.published ? "Yes" : "No"}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Ready</div>
                          <div>{property.ready ? "Yes" : "No"}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Edit</div>
                          <div>
                            {canManage ? (
                              <button
                                type="button"
                                className="text-secondaryColor"
                                onClick={async () => {
                                  const token = await getValidShortToken();
                                  if (token) {
                                    const url = `${process.env.REACT_APP_CLIENTSIDE_URL}/listing/create?propertyId=${property._id}&token=${token}`;
                                    window.open(url, "_blank");
                                  }
                                }}
                              >
                                Edit
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">View</div>
                          <div>
                            <button
                              type="button"
                              className="text-secondaryColor"
                              onClick={async () => {
                                const token = await getValidShortToken();
                                if (token) {
                                  const url = `${process.env.REACT_APP_CLIENTSIDE_URL}/rooms/${property._id}/preview?token=${token}`;
                                  window.open(url, "_blank");
                                }
                              }}
                            >
                              View
                            </button>
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Status</div>
                          <div>
                            <select
                              value={property.status}
                              onChange={(event) =>
                                handlePropertyStatus(
                                  property._id,
                                  event.target.value,
                                )
                              }
                              className="p-2 border border-gray-500 dark:border-white rounded-md dark:bg-darkBg2"
                            >
                              <option value={"pending"} disabled>
                                Select
                              </option>
                              <option value={"declined"}>Decline</option>
                              <option value={"approved"}>Approve</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </>
                  ))}
              </div>
            </div>
            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogContent className="max-w-screen w-[calc(100vw-20px)] lg:ml-[120px] lg:w-[700px] max-h-[calc(100vh-calc(100vh/3))] overflow-auto select-none bg-gray-200 dark:bg-[#171d37] dark:text-[#c7d8eb]">
              <DialogHeader className={"flex flex-row"}>
                <h2 className="dark:text-[#c7d8eb] min-w-fit">
                  Decline Edit request
                </h2>
                <div className="w-full flex justify-end">
                  <X
                    className="cursor-pointer dark:text-[#c7d8eb]"
                    onClick={() => {
                      setOpenDialog(false);
                    }}
                    size={30}
                  />
                </div>
              </DialogHeader>
              <hr className="border-t-[rgba(0,0,0,0.6)] dark:border-t-[rgba(255,255,255,0.6)] -mt-3" />
              <div className="flex w-full flex-col">
                <h2 className="text-xl dark:text-[#c7d8eb]">
                  <label htmlFor="reason">Reason</label>
                </h2>
                <p className="text-red-500 mb-0">*Required</p>
                <p className="text-red-500">*At least 20 characters</p>
                <div className="w-full">
                  <textarea
                    id="reason"
                    type="text"
                    placeholder="Reason"
                    className="w-full px-3 py-3 rounded-sm dark:bg-darkBg1 border border-[rgba(0,0,0,0.6)] dark:border-t-[rgba(255,255,255,0.6)]"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1 mt-1">
                  {reasonError && (
                    <p className="text-red-500">This field is required</p>
                  )}
                </div>
              </div>
              <hr className="border-t-[rgba(0,0,0,0.6)] dark:border-t-[rgba(255,255,255,0.6)]" />
              <DialogFooter>
                <button
                  className={`${
                    (reason &&
                      reason.length > 20 &&
                      "bg-primaryColor hover:bg-primaryColor") ||
                    "bg-primaryColorMuted hover:bg-primaryColorMuted"
                  } text-white px-4 py-2 rounded-md text-lg font-semibold`}
                  type="submit"
                  disabled={!(reason && reason.length > 20)}
                  onClick={() => {
                    declineEditRequest();
                  }}
                >
                  confirm
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <AlertDialog open={openAlertDialog} onOpenChange={setOpenAlertDialog}>
            <AlertDialogContent className="max-w-screen w-[calc(100vw-20px)] lg:w-[700px] max-h-[calc(100vh-calc(100vh/3))] overflow-auto select-none bg-gray-200 dark:bg-[#171d37] dark:text-[#c7d8eb]">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  <h2 className="dark:text-[#c7d8eb] min-w-fit">
                    Are you sure you want to approve this edit request
                  </h2>
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Approving this edit request will apply the host's changes to
                  the listing
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter
                className={"flex justify-end gap-2 items-center"}
              >
                <AlertDialogCancel>
                  <div onClick={() => setOpenAlertDialog(false)}>Cancel</div>
                </AlertDialogCancel>
                <AlertDialogAction>
                  <div
                    className="mt-0 text-white"
                    onClick={() => approveEditRequest()}
                  >
                    Continue
                  </div>
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EditRequests;
